/**
 * How much of the look sensitivity is left while the sights are up, for every mode at once.
 *
 * A scoped shot wants a slower hand than a hip-fired one, and how much slower is the player's own
 * figure rather than the gun's: weapons already carry `adsSensitivityScale`, and this multiplier
 * composes with it rather than replacing it, so a sniper scope still turns slower than a red dot
 * whatever the player set here.
 *
 * The scale rides `adsFraction`, the same blend the field of view uses, so a sight halfway up is
 * halfway slowed. Snapping at either end would put a sensitivity step in the middle of the raise,
 * which the hand feels as a hitch.
 *
 * Read per tick rather than captured, like every preference under `core/`: the settings screen
 * changes it with a game running behind it and the next frame turns at the new rate.
 */

import { clamp, lerp } from '@/sim/math';

/** Unchanged: the sights alone do not slow the hand until the player asks them to. */
export const DEFAULT_ADS_LOOK_MULTIPLIER = 1;

/** Below this a sighted turn is most of a desk, and the sights feel stuck rather than steady. */
export const MIN_ADS_LOOK_MULTIPLIER = 0.2;

/** Above this the sights come up faster than the hip, which nobody has ever asked a scope for. */
export const MAX_ADS_LOOK_MULTIPLIER = 1.5;

let multiplier = DEFAULT_ADS_LOOK_MULTIPLIER;

export function adsLookMultiplier(): number {
  return multiplier;
}

/** Clamped here as well as in the settings layer: this is the value the game actually turns on. */
export function setAdsLookMultiplier(value: number): void {
  multiplier = clamp(value, MIN_ADS_LOOK_MULTIPLIER, MAX_ADS_LOOK_MULTIPLIER);
}

/**
 * What a control map multiplies its look deltas by, for a body this far into the sights.
 *
 * A fraction outside 0 to 1 is pulled back in rather than extrapolated: a snapshot cannot honestly
 * mean more than fully sighted, and a scale past the multiplier would be a sensitivity no setting
 * chose.
 */
export function adsLookScale(adsFraction: number): number {
  return lerp(1, multiplier, clamp(adsFraction, 0, 1));
}
